import ProductAddToCartHOC from '@common/components/ProductAddToCartHOC';
import ProductCards from '@common/components/ProductCards';
import Product from '@common/models/Product';
import { RootState } from '@common/store';
import { addProductToCart, subtractProductFromCart } from '@common/store/cart/slice';
import { FeaturesSliceType } from '@common/store/features/slice';
import {
  fetchProductDetailsRequest,
  fetchProductReviewsRequest,
  ProductDetailsSliceType,
} from '@common/store/productDetails/slice';
import { StoreInfoSliceType } from '@common/store/storeInfo/slice';
import { addToast, removeToast, ToastWithKeyType } from '@common/store/toasts/slice';
import { isBrowser } from '@common/utils';
import QuickViewEventProduct from '@components/Event';
import QuickViewProduct from '@components/Product';
import ProductCardOverlay from '@components/ProductCardOverlay';
import Toasts from '@components/Toasts';
import Image from '@lib/Image';
import Close from '@Runway/images/Close.svg';
import React, { useState } from 'react';
import { connect } from 'react-redux';

import { EmptyProductCard } from '../../../../common/components/EmptyProductCard';
import { computeProductLink, PRODUCT_REFERRED_FROM } from '../../../../common/models/Product';

export interface ProductCardProps {
  product: Product;
  storeInfo: StoreInfoSliceType;
  cartProductQuantity: number;
  productDetails: ProductDetailsSliceType;
  addProductToCart: typeof addProductToCart;
  subtractProductFromCart: typeof subtractProductFromCart;
  fetchProductDetailsRequest: typeof fetchProductDetailsRequest;
  fetchProductReviewsRequest: typeof fetchProductReviewsRequest;
  toasts: ToastWithKeyType[];
  addToast: typeof addToast;
  removeToast: typeof removeToast;
  dimension?: string;
  referredFrom: PRODUCT_REFERRED_FROM;
  shopAll?: string;
  truncate?: number;
  features: FeaturesSliceType;
}

const ProductCard: React.FunctionComponent<ProductCardProps> = ({
  product,
  addProductToCart,
  subtractProductFromCart,
  cartProductQuantity,
  productDetails,
  toasts,
  removeToast,
  fetchProductDetailsRequest,
  fetchProductReviewsRequest,
  storeInfo,
  dimension = 'h-220 w-220 md:w-260 md:h-260',
  truncate = 23,
  referredFrom,
  shopAll,
  features,
}) => {
  if (shopAll === 'shopAll') {
    dimension = 'h-182 w-182 md:w-280 md:h-280';
  }

  if (!product) {
    return <EmptyProductCard dimensionW={shopAll === 'shopAll' ? 'w-182 md:w-280' : 'w-220 md:w-260'} />;
  }

  const [CloseDetails, SetCloseDetails] = useState(false);
  if (isBrowser()) {
    document.onkeyup = (e) => {
      if (e.key === 'Escape') {
        SetCloseDetails(false);
      }
    };
  }

  const computedProductLink = computeProductLink({ product, referredFrom });

  return (
    <>
      {CloseDetails && productDetails.productDetails && (
        <div
          className=" fixed z-50 inset-0 mx-auto h-full w-full py-50 overflow-scroll"
          style={{ background: 'rgba(0,0,0,.6)' }}
        >
          <div className="zoomIn bg-white md:mx-auto p-20 h-auto min-h-628 rounded-lg md:w-fit mt-100">
            <div className="w-full relative">
              <div className="mb-24 h-20">
                <Image
                  src={Close}
                  alt="Close"
                  className="absolute right-0 m-0 md:m-16 cursor-pointer h-16"
                  onClick={() => {
                    SetCloseDetails(!CloseDetails);
                    toasts.length ? toasts.map((t) => removeToast(t.key)) : null;
                  }}
                  noDefaultDimensions
                  noDefaultFit
                />
              </div>
            </div>
            {toasts.length ? (
              <div className="mt-6" id="QuickViewToasts">
                <Toasts toasts={toasts} addToast={addToast} removeToast={removeToast} quickView={true} />
              </div>
            ) : null}
            <ProductAddToCartHOC
              variantSetToMinQuantity={true}
              disableSuccessToasts={() => false}
              product={productDetails.productDetails}
              render={(hocArgs) => {
                const { finalProduct } = hocArgs;
                return (
                  <div>
                    {!product.isEvent ? (
                      <QuickViewProduct
                        subtractProductFromCart={subtractProductFromCart}
                        product={productDetails.productDetails}
                        addProductToCart={addProductToCart}
                        addToast={addToast}
                        cartProductQuantity={cartProductQuantity}
                        // isZoomEnabled={true}
                        onClose={() => {
                          SetCloseDetails(!CloseDetails);
                        }}
                        review={productDetails?.reviews?.length}
                        storeType={storeInfo.storeInfo?.storeType}
                        quickView={true}
                        finalProduct={finalProduct}
                        hocArgs={hocArgs}
                        productVideoFeature={features.features?.productVideo}
                      />
                    ) : (
                      <QuickViewEventProduct
                        subtractProductFromCart={subtractProductFromCart}
                        product={productDetails.productDetails}
                        addProductToCart={addProductToCart}
                        addToast={addToast}
                        onClose={() => {
                          SetCloseDetails(!CloseDetails);
                        }}
                        review={productDetails?.reviews?.length}
                        cartProductQuantity={cartProductQuantity}
                        storeType={storeInfo.storeInfo?.storeType}
                        quickView={true}
                        finalProduct={finalProduct}
                        hocArgs={hocArgs}
                        productVideoFeature={features.features?.productVideo}
                      />
                    )}
                  </div>
                );
              }}
            />
          </div>
        </div>
      )}

      <ProductCards
        product={product}
        addProductToCart={addProductToCart}
        referredFrom={referredFrom}
        mainDivClassNames=""
        imageDivClassNames="zoomIn rounded border border-disabled"
        discountPercentClassNames={'hidden'}
        titleClassName={
          'pt-26 font-bold leading-16 md:leading-19 text-productCardTitleSm md:text-productCardTitleMd mb-14 text-productCard-title line-clamp-1 break-word-only'
        }
        priceClassName={
          'text-productCard-finalPrice font-bold leading-24 text-productCardPriceSm md:text-productCardPriceMd mr-14'
        }
        discountPriceClassName={
          'line-through font-medium text-productCardDiscountPriceSm md:text-productCardDiscountPriceMd leading-24 text-productCard-mrp'
        }
        dimension={dimension}
        truncate={truncate}
        includeTaxClassName="hidden"
        quickViewDiv={
          <ProductCardOverlay
            product={product}
            fetchProductDetailsRequest={fetchProductDetailsRequest}
            fetchProductReviewsRequest={fetchProductReviewsRequest}
            removeToast={removeToast}
            toasts={toasts}
            CloseDetails={CloseDetails}
            SetCloseDetails={SetCloseDetails}
            computedProductLink={computedProductLink}
          />
        }
      />
    </>
  );
};

export default connect(
  ({ productDetails, toasts, features }: RootState) => ({
    productDetails,
    toasts,
    features,
  }),
  {
    fetchProductDetailsRequest,
    fetchProductReviewsRequest,
    removeToast,
  },
)(ProductCard);
